import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { api } from "@/lib/api";
import {
    usePreferenceScope,
    useStoredPreference,
} from "@/hooks/useStoredPreference";

export interface ListPage<T> {
    data: T[];
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    years?: string[];
    due_count?: number;
}
type QueryParams = Record<string, string | number | boolean | undefined>;
const cursorSchema = z.object({
    query: z.string(),
    page: z.number().int().min(1),
});

export const useDebouncedSearch = (search: string): string => {
    const [value, setValue] = useState(search);
    useEffect(() => {
        const timeout = window.setTimeout(() => setValue(search.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [search]);
    return value;
};

export const useServerList = <T>(
    key: string,
    endpoint: string,
    params: QueryParams
) => {
    const scope = usePreferenceScope();
    const { i18n } = useTranslation("global");
    const filterKey = JSON.stringify(params);
    const [cursor, setCursor] = useStoredPreference(
        `${key}:cursor`,
        cursorSchema,
        { query: filterKey, page: 1 },
        true
    );
    const [pageSize, setPageSize] = useStoredPreference(
        `${key}:page_size`,
        z.number().int().min(1).max(100),
        20
    );
    const page = cursor.query === filterKey ? cursor.page : 1;
    const setPage = (next: number): void =>
        setCursor({ query: filterKey, page: Math.max(1, next) });
    const query = useQuery({
        queryKey: ["list", key, scope, i18n.language, params, page, pageSize],
        queryFn: async ({ signal }): Promise<ListPage<T>> => {
            const response = await api.get<{ data: ListPage<T> }>(endpoint, {
                params: { ...params, page, per_page: pageSize, paginated: 1 },
                signal,
            });
            return response.data.data;
        },
        retry: false,
    });
    useEffect(() => {
        if (query.data && page > query.data.last_page) {
            setPage(Math.max(1, query.data.last_page));
        }
    }, [query.data, page]);
    return {
        items: query.data?.data || [],
        total: query.data?.total || 0,
        loading: query.isFetching,
        error: query.error instanceof Error ? query.error.message : null,
        metadata: query.data,
        reload: (): void => {
            void query.refetch();
        },
        pagination: {
            page,
            pageSize,
            total: query.data?.total || 0,
            onPage: setPage,
            onPageSize: (size: number): void => {
                setPageSize(size);
                setPage(1);
            },
        },
    };
};
